/**
 * FmThemeProvider — route data-theme owner.
 *
 * Architecture: this component does NOT theme the application through AntD.
 * AntD is allowed only for form controls; any unavoidable form-control theme
 * adapter must stay narrowly named and scoped near those controls.
 *
 * Data-theme: sets `data-theme` on <html> so CSS vars in tokens.css
 * ([data-theme="dark"] / [data-theme="light"]) resolve correctly. The app entry
 * resolves the route theme and passes it in, so this module stays free of router
 * state and DS renderers can import the island below.
 */
import React, { useEffect } from 'react';
import { RouteThemeContext } from './routeThemeContext';
import type { RouteTheme } from '@/shared/config/designTokens';

interface FmThemeProviderProps {
  theme: RouteTheme;
  children: React.ReactNode;
}

export const FmThemeProvider: React.FC<FmThemeProviderProps> = ({ theme, children }) => {
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  return <RouteThemeContext.Provider value={theme}>{children}</RouteThemeContext.Provider>;
};

export type FmThemePolarity = 'light' | 'dark';

interface FmThemeIslandProps {
  /** Polarity for this one surface; omitted, the surface keeps the ambient theme. */
  theme: FmThemePolarity | undefined;
  /** The single surface element that takes the polarity. */
  children: React.ReactElement;
}

/**
 * FmThemeIsland — the one sanctioned token-polarity island below the route theme.
 * Overlay and chrome surfaces (a drawer or card over a dark map, the mega-menu
 * under an adaptive top bar) must resolve light or dark independently of the
 * route. The theme owner applies `data-theme` to that surface, so no component
 * writes it itself (plan KTD3, Architecture Enforcement "Tone through the band").
 */
export const FmThemeIsland: React.FC<FmThemeIslandProps> = ({ theme, children }) =>
  theme ? React.cloneElement(children, { 'data-theme': theme } as React.HTMLAttributes<HTMLElement>) : children;

export default FmThemeProvider;
